import React, { useState, useRef, useEffect } from 'react';
import { IconBookmark, IconCalendarEvent, IconCheck, IconFileCheck, IconFilter, IconMapPin, IconRadar, IconStack2, IconTag, IconUsersGroup, IconX } from '@tabler/icons-react';
import { BoardStage } from '../../../entities/board';
import type { Group } from '../../../entities/group';
import type { Tag } from '../../../entities/tag';
import { FilterState } from '../model/filterStore';
import { fetchCityOptions, fetchSourceOptions } from '../../../entities/lead';
import { DateInput } from '../../../shared/ui/DateInput';
import { MultiInput } from '../../../shared/ui/MultiInput';
import { Select, SelectOption } from '../../../shared/ui/Select';
import { UserSearchInput } from '../../../shared/ui/UserSearchInput/UserSearchInput';
import { fetchUsersByIds } from '../../../entities/user/model/api';
import { pb } from '../../../shared/api/pocketbase';
import { CustomFilter, deleteCustomFilter, fetchCustomFilters, saveCustomFilter } from '../model/customFiltersApi';

interface FilterDropdownProps {
  isOpen: boolean;
  hasActiveFilters: boolean;
  initialFilters: FilterState;
  stages: BoardStage[];
  allTags: Tag[];
  groups?: Group[];
  showManagerFilter?: boolean;
  onToggle: () => void;
  onApply: (filters: FilterState) => void;
  onReset: () => void;
  onClose: () => void;
}

const EMPTY: FilterState = { userIds: [], groupIds: [], city: '', boardIds: [], createdFrom: '', createdTo: '', updatedFrom: '', updatedTo: '', noJobs: false, tagIds: [], sources: [], docTypes: [] };

const DOC_TYPE_OPTIONS: SelectOption[] = [
  { value: 'passport', label: 'Паспорт' },
  { value: 'ticket', label: 'Билет' },
  { value: 'attitude', label: 'Отношение' },
];

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  isOpen,
  hasActiveFilters,
  initialFilters,
  stages,
  allTags,
  groups,
  showManagerFilter = true,
  onToggle,
  onApply,
  onReset,
  onClose,
}) => {
  const [local, setLocal] = useState<FilterState>(initialFilters);
  const [initialUserOptions, setInitialUserOptions] = useState<SelectOption[]>([]);
  const [presets, setPresets] = useState<CustomFilter[]>([]);
  const [activePresetId, setActivePresetId] = useState('');
  const [saveName, setSaveName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const userId = pb.authStore.model?.id as string | undefined;

  useEffect(() => {
    setLocal(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    if (!initialFilters.userIds.length) return;
    fetchUsersByIds(initialFilters.userIds).then(users => {
      setInitialUserOptions(users.map(u => ({ value: u.id, label: u.name })));
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !userId) return;
    fetchCustomFilters(userId).then(setPresets);
  }, [isOpen, userId]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (isOpen && wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  const handleReset = () => {
    setLocal(EMPTY);
    setActivePresetId('');
    onReset();
  };

  const handleApplyManual = () => {
    setActivePresetId('');
    onApply(local);
  };

  const handlePresetChange = (id: string) => {
    if (!id) { setActivePresetId(''); return; }
    const preset = presets.find(p => p.id === id);
    if (!preset) return;
    setActivePresetId(id);
    setLocal(preset.filters);
    onApply(preset.filters);
  };

  const handleDeletePreset = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    e.preventDefault();
    await deleteCustomFilter(id);
    setPresets(prev => prev.filter(p => p.id !== id));
    if (activePresetId === id) setActivePresetId('');
  };

  const handleSave = async () => {
    if (!saveName.trim() || !userId) return;
    setSaving(true);
    try {
      const created = await saveCustomFilter(userId, saveName.trim(), local);
      setPresets(prev => [created, ...prev]);
      setActivePresetId(created.id);
      setSaveName('');
      setShowSaveInput(false);
    } finally {
      setSaving(false);
    }
  };

  const presetOptions: SelectOption[] = presets.map(p => ({ value: p.id, label: p.name }));

  return (
    <div className="filter-wrap" ref={wrapRef}>
      <button
        className={`btn-fi${isOpen ? ' active' : ''}`}
        onClick={onToggle}
        title="Фильтры"
        type="button"
      >
        <IconFilter size={16} stroke={2} />
        <div className={`fdot${hasActiveFilters ? ' on' : ''}`} />
      </button>
      <div className={`fdrop${isOpen ? ' open' : ''}`}>
        <div className="fdrop-title">Фильтрация</div>
        {presets.length > 0 && (
          <div className="fd-preset-field">
            <Select
              options={presetOptions}
              value={activePresetId}
              onChange={handlePresetChange}
              placeholder="Мои фильтры"
              icon={<IconBookmark size={15} stroke={1.9} />}
              renderOption={(opt, { selected }) => (
                <span className="fd-preset-opt">
                  <span className="fd-preset-opt-label">{opt.label}</span>
                  <span
                    className="fd-preset-opt-del"
                    onMouseDown={e => handleDeletePreset(e, opt.value)}
                  >
                    <IconX size={11} stroke={2.5} />
                  </span>
                </span>
              )}
            />
          </div>
        )}
        <div className="fd-grid">
          {showManagerFilter && (
            <div className="fd-field">
              <label>Менеджер</label>
              <UserSearchInput
                name="filter-manager"
                mode="multi"
                value={local.userIds}
                onChange={userIds => setLocal(prev => ({ ...prev, userIds }))}
                placeholder="Все менеджеры"
                initialOptions={initialUserOptions}
              />
            </div>
          )}
          {groups && groups.length > 0 && (
            <div className="fd-field">
              <label>Группа</label>
              <Select
                mode="multi"
                options={groups.map(g => ({ value: g.id, label: g.name || g.id }))}
                value={local.groupIds}
                onChange={groupIds => setLocal(prev => ({ ...prev, groupIds }))}
                placeholder="Все группы"
                icon={<IconUsersGroup size={15} stroke={1.9} />}
                searchable
              />
            </div>
          )}
          <div className="fd-field">
            <label>Город</label>
            <Select
              name="filter-city"
              fetchOptions={fetchCityOptions}
              fetchByValues={async values => values.map(v => ({ value: v, label: v }))}
              value={local.city}
              onChange={city => setLocal(prev => ({ ...prev, city }))}
              placeholder="Все города"
              icon={<IconMapPin size={15} stroke={1.9} />}
            />
          </div>
          <div className="fd-field">
            <label>Колонка</label>
            <MultiInput
              name="filter-board"
              options={stages.map(s => ({ value: s.id, label: s.name }))}
              value={local.boardIds}
              onChange={boardIds => setLocal(prev => ({ ...prev, boardIds }))}
              placeholder="Все колонки"
              icon={<IconStack2 size={15} stroke={1.9} />}
              searchable
            />
          </div>
          {allTags.length > 0 && (
            <div className="fd-field">
              <label>Теги</label>
              <Select
                mode="multi"
                options={allTags.map(t => ({
                  value: t.id,
                  label: t.name,
                  group: t.expand?.groupId?.name ?? t.groupId ?? '',
                }))}
                value={local.tagIds}
                onChange={tagIds => setLocal(prev => ({ ...prev, tagIds }))}
                placeholder="Все теги"
                icon={<IconTag size={15} stroke={1.9} />}
                searchable
                renderOption={(opt) => {
                  const tag = allTags.find(t => t.id === opt.value);
                  const c = tag?.colors;
                  return (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: c?.dot ?? 'var(--text3)' }} />
                      {opt.label}
                    </span>
                  );
                }}
              />
            </div>
          )}
          <div className="fd-field">
            <label>Источник</label>
            <Select
              mode="multi"
              fetchOptions={fetchSourceOptions}
              fetchByValues={async values => values.map(v => ({ value: v, label: v }))}
              value={local.sources}
              onChange={sources => setLocal(prev => ({ ...prev, sources }))}
              placeholder="Все источники"
              icon={<IconRadar size={15} stroke={1.9} />}
              searchable
            />
          </div>
          <div className="fd-field">
            <label>Документы</label>
            <Select
              mode="multi"
              options={DOC_TYPE_OPTIONS}
              value={local.docTypes ?? []}
              onChange={docTypes => setLocal(prev => ({ ...prev, docTypes: docTypes as FilterState['docTypes'] }))}
              placeholder="Любые документы"
              icon={<IconFileCheck size={15} stroke={1.9} />}
            />
          </div>
          <div className="fd-field fd-field-wide">
            <label>Дата создания</label>
            <div className="fd-date-range">
              <DateInput
                className="fd-sel"
                controlClassName="control-with-icon"
                icon={<IconCalendarEvent size={15} stroke={1.9} />}
                name="filter-created-from"
                value={local.createdFrom}
                max={local.createdTo || undefined}
                onChange={e => setLocal(prev => ({ ...prev, createdFrom: e.target.value }))}
              />
              <DateInput
                className="fd-sel"
                controlClassName="control-with-icon"
                icon={<IconCalendarEvent size={15} stroke={1.9} />}
                name="filter-created-to"
                value={local.createdTo}
                min={local.createdFrom || undefined}
                onChange={e => setLocal(prev => ({ ...prev, createdTo: e.target.value }))}
              />
            </div>
          </div>
          <div className="fd-field fd-field-wide">
            <label>Дата обновления</label>
            <div className="fd-date-range">
              <DateInput
                className="fd-sel"
                controlClassName="control-with-icon"
                icon={<IconCalendarEvent size={15} stroke={1.9} />}
                name="filter-updated-from"
                value={local.updatedFrom}
                max={local.updatedTo || undefined}
                onChange={e => setLocal(prev => ({ ...prev, updatedFrom: e.target.value }))}
              />
              <DateInput
                className="fd-sel"
                controlClassName="control-with-icon"
                icon={<IconCalendarEvent size={15} stroke={1.9} />}
                name="filter-updated-to"
                value={local.updatedTo}
                min={local.updatedFrom || undefined}
                onChange={e => setLocal(prev => ({ ...prev, updatedTo: e.target.value }))}
              />
            </div>
          </div>
          <div className="fd-field">
            <label>&#8203;</label>
            <button
              className={`fd-no-jobs${local.noJobs ? ' active' : ''}`}
              type="button"
              onClick={() => setLocal(prev => ({ ...prev, noJobs: !prev.noJobs }))}
            >
              <div className={`fd-no-jobs-cb${local.noJobs ? ' checked' : ''}`}>
                {local.noJobs && <IconCheck size={10} stroke={3} />}
              </div>
              Без задач
            </button>
          </div>
        </div>
        {showSaveInput ? (
          <div className="fd-save-row">
            <input
              className="fd-save-input"
              type="text"
              placeholder="Название фильтра"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setShowSaveInput(false); }}
              autoFocus
            />
            <button className="fd-save-confirm" type="button" onClick={handleSave} disabled={saving || !saveName.trim()}>
              {saving ? '...' : 'OK'}
            </button>
            <button className="fd-save-cancel" type="button" onClick={() => setShowSaveInput(false)}>
              <IconX size={14} stroke={2} />
            </button>
          </div>
        ) : (
          <button className="fd-save-btn" type="button" onClick={() => setShowSaveInput(true)}>
            Сохранить фильтр
          </button>
        )}
        <div className="fd-btns">
          <button className="fd-reset" onClick={handleReset} type="button">Сброс</button>
          <button className="fd-apply" onClick={handleApplyManual} type="button">Применить</button>
        </div>
      </div>
    </div>
  );
};

export { FilterDropdown };
